import React, { useState } from "react";

const LEGEND_ITEMS = [
  {
    label: "Parent\u2013Child Connection",
    visual: (
      <svg width="48" height="28" viewBox="0 0 48 28" fill="none">
        <path
          d="M24 2 C24 14, 24 14, 10 26"
          stroke="#fff"
          strokeWidth="2"
          fill="none"
        />
        <path
          d="M24 2 C24 14, 24 14, 38 26"
          stroke="#fff"
          strokeWidth="2"
          fill="none"
        />
      </svg>
    ),
    description:
      "Curved lines connect parents to their children, flowing downward through generations.",
  },
  {
    label: "Spouse / Partner",
    visual: (
      <svg width="48" height="12" viewBox="0 0 48 12" fill="none">
        <line x1="2" y1="6" x2="46" y2="6" stroke="#fff" strokeWidth="2" />
      </svg>
    ),
    description:
      "Straight horizontal lines connect married or partnered couples, shown side by side.",
  },
  {
    label: "Lineage Mode",
    // The tree's standout feature (Sam, 2026-09-27): framed in gold, but kept
    // in its place, since the entries around it relate to it.
    featured: true,
    visual: (
      <div className="flex flex-col items-center gap-y-[4px]">
        <div
          className="flex items-center justify-center rounded-[4px] text-[10px] font-display font-semibold"
          style={{
            padding: "2px 6px",
            background: "rgba(0, 9, 28, 0.8)",
            border: "1px solid #b5a03e",
            color: "#d4c26a",
          }}
        >
          Click
        </div>
        <svg width="48" height="28" viewBox="0 0 48 28" fill="none">
          <rect x="14" y="16" width="20" height="10" rx="3" stroke="#d4c26a" strokeWidth="1.5" fill="rgba(0,9,28,0.85)" />
          <line x1="24" y1="16" x2="24" y2="8" stroke="#d4c26a" strokeWidth="1.5" />
          <line x1="12" y1="8" x2="36" y2="8" stroke="#d4c26a" strokeWidth="1.5" />
          <rect x="2" y="0" width="18" height="8" rx="2" stroke="rgba(255,255,255,0.3)" strokeWidth="1" fill="rgba(0,9,28,0.85)" />
          <rect x="28" y="0" width="18" height="8" rx="2" stroke="rgba(255,255,255,0.3)" strokeWidth="1" fill="rgba(0,9,28,0.85)" />
        </svg>
      </div>
    ),
    description:
      "Click any card to open a full-screen view of that person's direct ancestry — parents, grandparents, and beyond. Hover any name to see a gold path tracing back to them. Once inside, click any ancestor to view THEIR ancestry; a breadcrumb trail at the top lets you navigate back, and “Back to Full Tree” returns you here.",
  },
  {
    label: "Gold Card Border",
    visual: (
      <div
        className="rounded-[3px]"
        style={{
          width: 28,
          height: 18,
          border: "3px solid #d4c26a",
          background: "rgb(120, 159, 172)",
        }}
      />
    ),
    description:
      "A gold border around a card means that person is on the direct path connecting to the bloodlines.",
  },
  {
    label: "Someone Who Appears Twice",
    visual: (
      <div
        className="rounded-[4px] px-[6px] py-[5px] text-[9px] leading-[1.25] text-center"
        style={{
          border: "1px solid rgba(255,255,255,0.3)",
          background: "rgba(0,9,28,0.85)",
          color: "#fff",
          width: 52,
        }}
      >
        <div style={{ fontWeight: 700 }}>Name</div>
        <div style={{ color: "#d4c26a", fontStyle: "italic", marginTop: 2 }}>
          (also appears above)
        </div>
      </div>
    ),
    description:
      "Cousins married cousins, so the same ancestor can often be reached through both a father's line and a mother's line. In the close-up view, the second appearance is simply marked “also appears above” rather than drawing that whole branch a second time.",
  },
  {
    label: "Search",
    visual: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
        <circle cx="10" cy="10" r="7" stroke="#d4c26a" strokeWidth="2" />
        <line
          x1="15"
          y1="15"
          x2="21"
          y2="21"
          stroke="#d4c26a"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
    description:
      "Use the search bar above the tree to find a family member by name. Select them to jump directly to their place in the tree.",
  },
  {
    label: "Zoom Controls",
    visual: (
      <div
        className="flex flex-col items-center"
        style={{ fontSize: 11, fontWeight: 700, color: "#b5a03e" }}
      >
        <span>+</span>
        <div
          style={{
            width: 4,
            height: 24,
            borderRadius: 2,
            background: "rgba(181, 160, 62, 0.4)",
            margin: "2px 0",
          }}
        />
        <span>{"\u2212"}</span>
      </div>
    ),
    description:
      "Drag the slider (bottom right) up to zoom in, down to zoom out. You can also scroll with your mouse wheel or pinch on a touchscreen.",
  },
  {
    label: "Why Lines Go Through Certain Names",
    visual: (
      <div
        className="flex items-center justify-center rounded-full text-[14px] font-display font-bold"
        style={{
          width: 24,
          height: 24,
          border: "2px solid #b5a03e",
          color: "#b5a03e",
        }}
      >
        ?
      </div>
    ),
    description:
      // Said as the tree really behaves: it is always drawn from the founder
      // (treeRoot.js), opens centred on you, and a card click opens that
      // person's ancestry (the entry above) rather than re-centring the tree.
      "The whole tree is drawn outward from the family's founder, so every line you see traces back through the founder's descendants — that is why lines pass through certain names. It opens centered on you, and Reset Tree brings you back. Clicking a card opens that person's ancestry (see above) rather than moving the tree.",
  },
];

// A small gold fleur-de-lis that sits on the featured box's border, on a
// navy ground so it reads as set into the line. Drawn, not an emoji, so it is
// gold on every device.
function Fleur({ className = "" }) {
  return (
    <span
      className={"flex items-center justify-center px-[6px] " + className}
      style={{ background: "#00091c" }}
      aria-hidden="true"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="#d4c26a">
        <path d="M12 2c-1.3 1.6-2 3.3-2 5.1 0 1.5.6 2.8 1.3 3.9H9.2c-.6-1.8-2-3.2-3.9-3.2C3.7 7.8 2.5 9 2.5 10.6c0 1.5 1.1 2.7 2.6 2.7.4 0 .8-.1 1.1-.2-.2 1.5.6 2.9 2 3.4h2.3v1.3H8v1.6h2.5V22h3v-2.6H16v-1.6h-2.5v-1.3h2.3c1.4-.5 2.2-1.9 2-3.4.3.1.7.2 1.1.2 1.5 0 2.6-1.2 2.6-2.7 0-1.6-1.2-2.8-2.8-2.8-1.9 0-3.3 1.4-3.9 3.2h-2.1c.7-1.1 1.3-2.4 1.3-3.9 0-1.8-.7-3.5-2-5.1zm-6.9 9.9c-.6 0-1-.5-1-1.1 0-.7.5-1.2 1.1-1.2 1 0 1.9.9 2.3 2.1-.6.1-1.5.2-2.4.2zm13.8 0c-.9 0-1.8-.1-2.4-.2.4-1.2 1.3-2.1 2.3-2.1.6 0 1.1.5 1.1 1.2 0 .6-.4 1.1-1 1.1zM12 5.1c.4.7.6 1.3.6 2 0 1.3-.3 2.5-.6 3.5-.3-1-.6-2.2-.6-3.5 0-.7.2-1.3.6-2zm-3.4 9.4c-.4-.3-.6-.8-.4-1.4h7.6c.2.6 0 1.1-.4 1.4H8.6z"/>
      </svg>
    </span>
  );
}

function TreeLegend() {
  const [expanded, setExpanded] = useState(false);

  return (
    // Lives inline in the tree's top bar, beside Admin Mode / Reset Tree. The panel
    // is positioned against THIS wrapper, so it drops straight down from the button
    // and can never collide with the search row or the Admin Mode banner — which is
    // what happened when the legend was absolutely positioned over the canvas.
    <div className="relative" onClick={(e) => e.stopPropagation()}>
      {/* Toggle button — styled to match its sibling pills in the top bar */}
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        className="flex items-center gap-x-[6px] rounded-full px-[12px] py-[6px] font-display text-[12px] font-semibold transition-colors duration-200 whitespace-nowrap"
        style={{
          background: "#b5a03e",
          border: "1px solid #b5a03e",
          color: "#00091c",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#d4c26a";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "#b5a03e";
        }}
      >
        <span
          className="text-[10px] transition-transform duration-200"
          style={{
            display: "inline-block",
            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
          }}
          aria-hidden="true"
        >
          &#9654;
        </span>
        How to Read This Tree
      </button>

      {/* Legend panel — hangs from the button, right-aligned so it cannot run off
          the edge of the screen (the button sits near the right of the top bar). */}
      {expanded && (
        <div
          className="absolute right-0 top-full z-40 mt-[8px] flex flex-col gap-y-[4px] rounded-[12px] p-[22px] text-left"
          style={{
            // Solid, not translucent: a backdrop blur behind text makes it look
            // soft and smeared. This panel is for reading.
            background: "#00091c",
            border: "1px solid #b5a03e",
            boxShadow: "0 10px 32px rgba(0, 0, 0, 0.6)",
            width: "min(400px, calc(100vw - 32px))",
            maxHeight: "min(70vh, 560px)",
            overflowY: "auto",
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
          }}
          role="region"
          aria-label="Family tree legend"
        >
          <h3
            className="text-[18px] font-display font-bold mb-[4px]"
            style={{ color: "#d4c26a", letterSpacing: "0.01em" }}
          >
            Reading the Family Tree
          </h3>
          <div
            className="mb-[12px]"
            style={{
              height: 1,
              background:
                "linear-gradient(to right, #b5a03e 0%, rgba(181,160,62,0.2) 100%)",
            }}
          />

          {LEGEND_ITEMS.map((item, i) => item.featured ? (
            // Lineage Mode, framed: a gold border with a fleur-de-lis set into
            // the top edge (Sam, 2026-09-27; no fleur on the bottom).
            <div
              key="featured"
              className="relative mt-[10px] mb-[10px] rounded-[8px] px-[14px] pt-[20px] pb-[14px]"
              style={{
                border: "1.5px solid #d4c26a",
                boxShadow: "inset 0 0 0 3px #00091c, inset 0 0 0 4px rgba(212, 194, 106, 0.45)",
                background: "rgba(181, 160, 62, 0.08)",
              }}
            >
              <Fleur className="absolute left-1/2 -translate-x-1/2 -top-[11px]" />
              <div className="flex items-start gap-x-[14px]">
                <div className="flex-shrink-0 flex items-center justify-center w-[52px]">
                  {item.visual}
                </div>
                <div className="flex flex-col gap-y-[4px] min-w-0">
                  <span
                    className="text-[16px] font-display font-bold"
                    style={{ color: "#f0e2a0", letterSpacing: "0.01em" }}
                  >
                    {item.label}
                  </span>
                  <span
                    className="text-[14px] leading-[1.55]"
                    style={{
                      color: "rgba(255,255,255,0.96)",
                      fontFamily:
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                    }}
                  >
                    {item.description}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div
              key={i}
              className="flex items-start gap-x-[14px] py-[13px]"
              style={
                i < LEGEND_ITEMS.length - 1
                  ? { borderBottom: "1px solid rgba(255,255,255,0.10)" }
                  : {}
              }
            >
              <div className="flex-shrink-0 flex items-center justify-center w-[52px]">
                {item.visual}
              </div>
              <div className="flex flex-col gap-y-[4px]">
                <span
                  className="text-[14px] font-display font-bold"
                  style={{ color: "#d4c26a", letterSpacing: "0.01em" }}
                >
                  {item.label}
                </span>
                {/* Sans, not the serif body face: small serif on a dark ground
                    reads muddy. Near-white, larger, roomier line-height. */}
                <span
                  className="text-[14px] leading-[1.55]"
                  style={{
                    color: "rgba(255,255,255,0.94)",
                    fontFamily:
                      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                  }}
                >
                  {item.description}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TreeLegend;
